import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from "react-router-dom";
import { ogafAction } from "@ogaf/react.scripts/Utils/ogafAction";

/**
 * Komponenta oblibených položek.
 */

/**
 * Objekt pro komunikaci s backendem.
 */
interface FavoritesData {
    /**
     * V případě true, přidá na první pozici možnost přidání aktuální stránky.
     */
    NewEntryShow: boolean;
    /**
     * Text který se zobrazuje uživateli.
     */
    NewEntryCaption: string;
    /**
     * Seznam již exisujících oblíbených položek.
     */
    FavoritesEntries: FavoriteEntry[];
}

/**
 * Objekt oblíbené položky.
 */
interface FavoriteEntry {
    /**
     * Text který se zobrazuje uživateli.
     */
    Caption: string;
    /**
     * Link, url která se vkládá do vlastnosti href u html elementu a.
     */
    Link: string;
    /**
     * Pořadí položky. Před vykreslením se provede řazení.
     */
    Order: number;
}

/**
 * Props komponenty Favorites.
 */
interface FavoritesProps {
    /**
     * Id komponenty, defaultně "favoritesComponent".
     */
    id?: string;
    /**
     * Funkce pro získání dat oblíbených položek.
     * @returns Promise s daty oblíbených položek.
     */
    getFetchData?: () => Promise<FavoritesData>;
    /**
     * Jméno zprávy pro získání dat oblíbených položek, defaultně "msg_getFavoriteEntries".
     */
    getCustomMessageType?: string;
    /**      
     * Data pro získání oblíbených položek.
     * */
    getMsgData: Record<string, string>;
    /**
     * Funkce pro uložení dat oblíbených položek.
     * @param action - akce, která se má provést, např. "add" nebo "delete".
     * @param url - url položky, která se má přidat nebo odstranit.
     * @returns Promise s aktualizovanými daty oblíbených položek.
     */
    setFetchData?: (action: string, url: string) => Promise<FavoritesData>;
    /**
     * Jméno zprávy pro uložení dat oblíbených položek, defaultně "msg_setFavoriteEntries".
     */
    setCustomMessageType?: string;
    /**
     * Data pro uložení oblíbených položek.
     */
    setMsgData: Record<string, string>;
}

/**
 * Komponenta pro zobrazení oblíbených položek.
 * @param param0 - Props komponenty Favorites.
 * @returns
 */
const Favorites: React.FC<FavoritesProps> = ({
    id = "favoritesComponent",
    getFetchData,
    getCustomMessageType = "msg_getFavoriteEntries",
    getMsgData,
    setFetchData,
    setCustomMessageType = "msg_setFavoriteEntries",
    setMsgData
}) => {
    const [isShowPopup, setIsShowPopup] = useState(false);
    const [data, setData] = useState<FavoritesData | null>(null);
    const contentRef = useRef<HTMLDivElement | null>(null);

    /**
     * Ala konstruktor, načte data při prvním renderu komponenty.
     */
    useEffect(() => {
        loadData();
    }, []);

    /**
     * Kontroluje kliknutí mimo komponentu a zavře popup, pokud je otevřený.
     */
    useEffect(() => {
        if (!isShowPopup) {
            return;
        }

        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as Node;

            if (contentRef.current && !contentRef.current.contains(target)) {
                setIsShowPopup(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [isShowPopup]);

    /**
     * Načte data oblíbených položek z backendu.
     */
    const loadData = async () => {
        try {
            let data: Record<string, string> = {};
            if (getMsgData) {
                data = getMsgData;
            }

            const result: FavoritesData = getFetchData
                ? await getFetchData()
                : await ogafAction<FavoritesData>(getCustomMessageType, data, "/Favorites/DigestMessage");
            setData(result);            
        } catch (error) {
            console.error('Error fetching data for Favorites:', error);
        }
    };

    /**
     * Uloží data oblíbených položek na backend.
     * @param action - akce, která se má provést, např. "add" nebo "delete".
     * @param link - link položky, která se má přidat nebo odstranit.
     */
    const saveData = async (action: string, link: string) => {
        try {
            let result: FavoritesData;
            if (setFetchData) {
                result = await setFetchData(action, link);
            } else {
                const data: Record<string, string> = { ...setMsgData, action, link };
                result = await ogafAction<FavoritesData>(setCustomMessageType, data, "/Favorites/DigestMessage");
            }
            setData(result);
        } catch (error) {
            console.error('Error saving data for Favorites:', error);
        }
    };

    /**
     * Pomocná funkce pro přidání aktuální stránky do oblíbených položek.
     */
    const handleAdd = () => {
        saveData('add', location.pathname);
    };

    /**
     * Pomocná funkce pro odstranění položky z oblíbených položek.
     * @param link - link položky, která se má odstranit.
     */
    const handleDelete = (link: string) => {
        saveData('delete', link);
    };

    /**
     * Uloží si aktuální položku. Nelze přidat home ('/')
     */
    const currentPath = location.pathname;

    /**
     * Renderuje obsah komponenty, včetně seznamu oblíbených položek a možnosti přidání aktuální stránky.
     * @returns JSX.Element obsahující seznam oblíbených položek a možnost přidání aktuální stránky.
     */
    const renderContent = () => {
        return (
            <div
                ref={contentRef}
                className={`favorites-content${isShowPopup ? '' : ' d-none'}`}
            >
                {data?.NewEntryShow && currentPath!=="/" &&
                    <div className="item new" onClick={handleAdd}><span>{data.NewEntryCaption}</span><span className="icon bi bi-plus-square"></span></div>                    
                }
                {data?.FavoritesEntries
                    ?.slice()
                    .sort((a, b) => a.Order - b.Order)
                    .map((entry) => (
                        <div className="item" key={`item-${entry.Order}`}>
                            <a                               
                                href={entry.Link}
                                className="link"
                            >
                                {entry.Caption}
                            </a>
                            <span className="ms-2 icon bi bi-dash-square" onClick={() => handleDelete(entry.Link)}></span>
                        </div>
                    ))}
                {data?.FavoritesEntries && data?.FavoritesEntries.length <= 0 &&
                    <div className="item nodata text-center">Žádné oblíbené položky</div> 
                }
            </div>
        )
    };

    /**
     * Vstupní bod komponenty, vykreslí ikonu oblíbených položek a při najetí myší zobrazí seznam oblíbených položek.
     */
    return (
        <div
            className="favorites bi bi-star"
            id={id}
            onClick={() => setIsShowPopup(true)}
        >
            {renderContent()}
        </div>
    );
};

export default Favorites;